/* eslint-disable react-hooks/exhaustive-deps */
import './styles/App.scss';
// Os campos do design system estão em quase toda tela carregada sob demanda. Entrando aqui, a
// folha deles fica no pacote principal: em cada pedaço, a ordem dela com as outras folhas
// variava, e o build (CI) reprova "Conflicting order".
import './components/ds/campos.module.scss';
// ⚠️ ANDAIME DA MIGRAÇÃO: a marca antiga e o banner de instalação são usados só por telas antigas
// carregadas sob demanda (landing, criar artista, desbloqueio); sem a casca antiga no pacote
// principal, a ordem das duas folhas variava entre os pedaços e o build (CI) reprovava "Conflicting
// order". Saem com as telas que as usam.
import './components/MaestraBrand/MaestraBrand.module.scss';
import './components/PwaInstallBanner/styles.scss';

import i18next from 'i18next';
import { FC, Suspense, lazy, useEffect, useState } from 'react';

import {
  Navigate,
  Outlet,
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';

import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { persistor, store, useAppDispatch, useAppSelector } from '@maestra/core/store/store';
import { useAdminRole, type ModuloAdmin } from '@maestra/core/hooks/useAdminRole';
import { authActions } from '@maestra/core/store/slices/auth';

import { supabase } from '@maestra/core/lib/supabase';
import { ConsentProvider, useConsent } from '@maestra/core/hooks/useConsent';
import { AberturaDaMarca, CarregandoApp, CarregandoPagina } from './ui-carbon/padroes/Carregando';
import { TemaProvider } from './logica/tema/TemaProvider';
import { AvisosDoCarbon } from './ui-carbon/padroes/avisos';
import { Casca } from './ui-carbon/casca/Casca';
import { Legado } from './pages/_legado/Legado';
import { RequireArtistActive } from './components/RequireArtistActive';
import { RequireModuloLiberado } from './components/RequireModuloLiberado';

// Pages
import Login from './ui-carbon/telas/auth/Entrar';
import Signup from './ui-carbon/telas/auth/Cadastro';
import Welcome from './ui-carbon/telas/auth/BoasVindas';
import Consent from './ui-carbon/telas/auth/Consentimento';
import ContaBloqueada from './ui-carbon/telas/auth/ContaBloqueada';
import { ModalDoCodigoLigado } from './ui-carbon/telas/dinheiro/ModalDoCodigo';
// A landing no Carbon (ui-carbon/telas/publicas/landing), aprovada pelo Lucas em 27/09/2026 depois
// de validada na /nova-landing. A antiga (pages/Landing) saiu, e com ela o cabeçalho e o rodapé
// antigos: as outras páginas públicas usam a mesma moldura (ui-carbon/telas/publicas/Moldura).
const Landing = lazy(() => import('./ui-carbon/telas/publicas/landing'));
const ModuloPublico = lazy(() => import('./ui-carbon/telas/publicas/modulo'));
// ⚠️ A ROTA /index2 SAIU DO AR. `src/pages/Landing2` continua no repositório como referência de
// layout, mas deixou de ser servida: ela não era linkada de lugar nenhum e, mesmo assim, era
// pública e indexável, anunciando "Perfis ilimitados" e os preços do modelo de pagamento único.
// Uma página de estudo que ninguém revisa não pode ficar acessível ao público falando de preço.
// Para voltar a olhá-la, monte o componente numa rota local em vez de republicar este endereço.
// As páginas públicas no Carbon, na mesma moldura da landing (ui-carbon/telas/publicas).
const Sobre = lazy(() => import('./ui-carbon/telas/publicas/sobre'));
const MusicRioAcademy = lazy(() => import('./ui-carbon/telas/publicas/musicRioAcademy'));
const ResetPassword = lazy(() => import('./ui-carbon/telas/auth/RedefinirSenha'));
const ForgotPassword = lazy(() => import('./ui-carbon/telas/auth/EsqueciSenha'));
const AuthCallback = lazy(() => import('./ui-carbon/telas/auth/RetornoDoLogin'));
const Page404 = lazy(() => import('./ui-carbon/telas/conta/NaoEncontrada'));
const Artists = lazy(() => import('./ui-carbon/telas/perfis/SeusPerfis'));
const Inicio = lazy(() => import('./ui-carbon/telas/perfil/Inicio'));
const Diagnostico = lazy(() => import('./ui-carbon/telas/perfil/Diagnostico'));
const Planejamento = lazy(() => import('./ui-carbon/telas/perfil/Planejamento'));
const PlanoDeAcao = lazy(() => import('./ui-carbon/telas/perfil/PlanoDeAcao'));
const Catalog = lazy(() => import('./ui-carbon/telas/perfil/Musicas'));
const Lancamentos = lazy(() => import('./ui-carbon/telas/perfil/Lancamentos'));
const LancamentoLigado = lazy(() => import('./ui-carbon/telas/perfil/Lancamentos').then((m) => ({ default: m.LancamentoLigado })));
const Arquivos = lazy(() => import('./ui-carbon/telas/perfil/Arquivos'));
const Negociacoes = lazy(() => import('./ui-carbon/telas/perfil/Crm'));
const Shows = lazy(() => import('./ui-carbon/telas/perfil/Shows'));
const PaginaDoShow = lazy(() => import('./ui-carbon/telas/perfil/Show'));
const CronogramaDoShow = lazy(() => import('./ui-carbon/telas/perfil/Cronograma'));
const CronogramaDaEquipe = lazy(() => import('./ui-carbon/telas/perfil/CronogramaDaEquipe'));
const PropostaPublica = lazy(() => import('./pages/PropostaPublica'));
const PerfilDoArtista = lazy(() => import('./ui-carbon/telas/publicas/PerfilDoArtista'));
const PlaylistPublica = lazy(() => import('./ui-carbon/telas/publicas/Playlist'));
const BrandingPublico = lazy(() => import('./ui-carbon/telas/publicas/BrandingPublico'));
const PressKitPublico = lazy(() => import('./ui-carbon/telas/publicas/PressKitPublico'));
const MaterialDeVendaPublico = lazy(() => import('./ui-carbon/telas/publicas/PressKitPublico').then((m) => ({ default: m.MaterialDeVendaPublicoLigado })));
const FotosDoPressKit = lazy(() => import('./ui-carbon/telas/publicas/PressKitPublico').then((m) => ({ default: m.FotosPublicasLigado })));
const PerfilPublico = lazy(() => import('./ui-carbon/telas/perfil/PerfilPublico'));
const FichaPublica = lazy(() => import('./pages/FichaPublica'));
const CronogramaPublico = lazy(() => import('./pages/CronogramaPublico'));
const EditorDaMusica = lazy(() => import('./ui-carbon/telas/perfil/Editor'));
const Agenda = lazy(() => import('./ui-carbon/telas/perfil/Agenda'));
const Team = lazy(() => import('./ui-carbon/telas/perfil/Equipe'));
const EmBreve = lazy(() => import('./ui-carbon/telas/perfil/EmBreve/EmBreve').then((m) => ({ default: m.EmBreve })));
const BrandingDoMarketing = lazy(() => import('./ui-carbon/telas/perfil/Marketing/BrandingLigado'));
const CriarBranding = lazy(() => import('./ui-carbon/telas/marketing/CriarBranding'));
const CriarMaterial = lazy(() => import('./ui-carbon/telas/marketing/CriarMaterial'));
const MaterialDeApresentacao = lazy(() => import('./ui-carbon/telas/perfil/Marketing/MaterialLigado'));
const MaterialDeVenda = lazy(() => import('./ui-carbon/telas/perfil/Marketing/MaterialDeVendaLigado'));
const CriarRedes = lazy(() => import('./ui-carbon/telas/marketing/CriarRedes'));
const PlanoDeRedes = lazy(() => import('./ui-carbon/telas/perfil/Marketing/PlanoDeRedesLigado'));
const GestaoDasRedes = lazy(() => import('./ui-carbon/telas/perfil/Marketing/GestaoDasRedesLigado'));
const RedesPublico = lazy(() => import('./ui-carbon/telas/publicas/RedesPublico'));
const Nyta = lazy(() => import('./ui-carbon/telas/nyta'));
const Settings = lazy(() => import('./ui-carbon/telas/conta/Configuracoes'));
const Support = lazy(() => import('./ui-carbon/telas/conta/Suporte'));
const Legal = lazy(() => import('./pages/Legal'));
const Notifications = lazy(() => import('./ui-carbon/telas/conta/Notificacoes'));
const Wizard = lazy(() => import('./ui-carbon/telas/wizard'));
const AdminKnowledgeBase = lazy(() => import('./ui-carbon/telas/admin/Ligadas').then((m) => ({ default: m.BaseDeConhecimentoLigada })));
const AdminDashboard = lazy(() => import('./ui-carbon/telas/admin/Ligadas').then((m) => ({ default: m.DashboardLigado })));
const AdminCoupons = lazy(() => import('./ui-carbon/telas/admin/Ligadas').then((m) => ({ default: m.CuponsLigados })));
const AdminAccessPasses = lazy(() => import('./ui-carbon/telas/admin/Ligadas').then((m) => ({ default: m.CodigosDeAcessoLigado })));
const AdminUsers = lazy(() => import('./ui-carbon/telas/admin/Ligadas').then((m) => ({ default: m.UsuariosLigados })));
const AdminSales = lazy(() => import('./ui-carbon/telas/admin/Ligadas').then((m) => ({ default: m.VendasLigadas })));
const AdminAcessos = lazy(() => import('./ui-carbon/telas/admin/Ligadas').then((m) => ({ default: m.AcessosLigados })));
const AdminModelosDaIa = lazy(() => import('./ui-carbon/telas/admin/Ligadas').then((m) => ({ default: m.ModelosDaIaLigado })));
const AdminArtists = lazy(() => import('./ui-carbon/telas/admin/Ligadas').then((m) => ({ default: m.PerfisDoAdminLigados })));
const AdminReviews = lazy(() => import('./ui-carbon/telas/admin/Ligadas').then((m) => ({ default: m.AvaliacoesLigadas })));
const AdminPush = lazy(() => import('./ui-carbon/telas/admin/Ligadas').then((m) => ({ default: m.PushLigado })));
const SubscriptionPage = lazy(() => import('./ui-carbon/telas/dinheiro/Ligadas').then((m) => ({ default: m.PlanosLigados })));
const AjustarPlanoPage = lazy(() => import('./ui-carbon/telas/dinheiro/Ligadas').then((m) => ({ default: m.AjustarPlanoLigado })));
const SubscriptionSuccessPage = lazy(() => import('./ui-carbon/telas/dinheiro/Ligadas').then((m) => ({ default: m.SucessoDaAssinaturaLigado })));
const PaymentPage = lazy(() => import('./ui-carbon/telas/dinheiro/Ligadas').then((m) => ({ default: m.PagamentoPixLigado })));
const ArtistCreate = lazy(() => import('./ui-carbon/telas/criarArtista'));
const ProfileUnlock = lazy(() => import('./pages/ProfileUnlock'));
const Payments = lazy(() => import('./ui-carbon/telas/dinheiro/Ligadas').then((m) => ({ default: m.PagamentosLigados })));
// A vitrine da UI nova (src/ui-carbon/vitrine): só no desenvolvimento. No build de produção o
// `NODE_ENV` vira 'production' e o import some do pacote.
const Vitrine = process.env.NODE_ENV === 'development' ? lazy(() => import('./ui-carbon/vitrine/Vitrine')) : null;

// ---- Recovery hash guard -------------------------------------------------------------------

/**
 * O link de "redefinir senha" do Supabase devolve os tokens no HASH da URL
 * (#access_token=...&type=recovery). O ideal é o Supabase redirecionar direto pra
 * /redefinir-senha, mas se a URL de redirect não estiver na allowlist do Auth ele cai no
 * Site URL (o domínio raiz) — e o token acaba na landing (/), que não sabe tratá-lo.
 *
 * Este guard roda em QUALQUER rota: se achar um token de recovery no hash e não estivermos
 * já em /redefinir-senha, reencaminha pra lá preservando o hash. Assim o fluxo funciona
 * independentemente da config de redirect do Supabase (belt-and-suspenders).
 */
const isRecoveryHash = (hash: string): boolean =>
  hash.includes('access_token=') && hash.includes('type=recovery');

// Retorno do login social (Google) no fluxo IMPLICIT: os tokens voltam no hash
// (#access_token=...&refresh_token=...), SEM type=recovery. Igual ao recovery, se a URL de
// redirect não estiver na allowlist do Supabase o retorno cai no Site URL (a landing /) — então
// capturamos em qualquer rota e reencaminhamos pra /auth/callback, que estabelece a sessão.
const isOAuthHash = (hash: string): boolean =>
  hash.includes('access_token=') && !hash.includes('type=recovery');

const RecoveryHashGuard: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const hash = window.location.hash;
    if (location.pathname !== '/redefinir-senha' && isRecoveryHash(hash)) {
      // Preserva o hash (tokens) ao trocar de rota — a ResetPassword lê de window.location.hash.
      navigate(`/redefinir-senha${hash}`, { replace: true });
    } else if (location.pathname !== '/auth/callback' && isOAuthHash(hash)) {
      // Preserva o hash até a rota de callback, que lê os tokens e faz o setSession.
      navigate(`/auth/callback${hash}`, { replace: true });
    }
  }, [location.pathname, navigate]);

  return null;
};

// ---- Auth ----------------------------------------------------------------------------------

const AuthListener: FC = () => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(authActions.bootstrapSession());

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      // E-mail não confirmado não conta como logado (precisa do código de verificação no cadastro).
      dispatch(authActions.setSession({ session: authActions.confirmedOrNull(session ?? null) }));
    });

    return () => sub.subscription.unsubscribe();
  }, [dispatch]);

  return null;
};

const RequireAuth: FC = () => {
  const user = useAppSelector((s) => s.auth.user);
  const requesting = useAppSelector((s) => s.auth.requesting);

  // A sessão ainda voltando: é a abertura do app, com a marca (e não a casca vazia).
  if (requesting && user === undefined) {
    return <CarregandoApp />;
  }
  if (!user) return <Navigate to='/login' replace />;
  // O provider carrega o estado de consentimento uma vez para o gate e para a tela de coleta.
  return (
    <ConsentProvider>
      <Outlet />
    </ConsentProvider>
  );
};

// Gate de consentimento (LGPD). Os Termos (§3) e a Política (§11) restringem o uso a maiores de
// 18 anos; este componente é o que torna isso real, e cobre de uma vez os três caminhos de
// entrada: cadastro por e-mail, login por Google (que não passa por formulário) e as contas
// criadas antes da exigência, que caem aqui no próximo acesso.
const RequireConsent: FC = () => {
  const { state, loading, unavailable } = useConsent();

  if (loading) return <CarregandoApp />;

  // Indisponibilidade transitória não tranca o app: um deploy ruim da função deixaria todos os
  // usuários de fora, o que é pior que o risco coberto aqui. A exigência volta na próxima
  // verificação bem-sucedida, e a escrita de dados segue protegida pela RLS de qualquer forma.
  if (unavailable) return <Outlet />;

  if (state?.blocked) return <Navigate to='/conta-bloqueada' replace />;
  if (state && !state.satisfied) return <Navigate to='/consentimento' replace />;
  return <Outlet />;
};

// Admin guard: verifica se o usuário logado é admin.
// Checa app_metadata do JWT (mais rápido) OU a tabela platform_admins (fallback).
// `null` enquanto confere: a rota espera, em vez de mandar embora o admin cuja flag não está no token.
const useEhAdmin = (): boolean | null => {
  const user = useAppSelector((s) => s.auth.user);
  const session = useAppSelector((s) => s.auth.session);
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);

  useEffect(() => {
    if (!user) return;

    // Primeiro: checar app_metadata no JWT (instantâneo, sem query)
    const appMeta = user.app_metadata || {};
    if (appMeta.is_platform_admin) {
      setIsAdmin(true);
      return;
    }

    // Fallback: checar tabela platform_admins
    supabase
      .from('platform_admins')
      .select('id')
      .eq('user_id', user.id)
      .maybeSingle()
      .then(({ data, error }) => {
        if (error) console.warn('[RequireAdmin] query error:', error.message);
        setIsAdmin(!!data);
      });
  }, [user, session]);

  return isAdmin;
};

const RequireAdmin: FC = () => {
  const isAdmin = useEhAdmin();
  if (isAdmin === null) return <CarregandoPagina />;
  if (!isAdmin) return <Navigate to='/artists' replace />;
  return <Outlet />;
};

// Telas do admin que o time de vendas NAO alcanca. Vendedor precisa do CRM, e nada mais: aqui
// moram exclusao de conta, cupons, disparo de push e dados pessoais de toda a base.
//
// Isto e conveniencia de navegacao, nao seguranca. Quem barra de verdade e a RLS: as tabelas
// `sales_*` liberam por `can_use_sales_crm()`, e as demais nem para vendedor nem para anonimo.
const RequireFullAdmin: FC = () => {
  const { carregando, ehAdminPleno, modulos } = useAdminRole();
  if (carregando) return <CarregandoPagina />;
  // Quem nao e admin pleno cai no primeiro modulo que alcanca. Mandar para /admin/vendas fixo
  // deixaria um analista de suporte, que nao tem o CRM, girando entre dois redirecionamentos.
  if (!ehAdminPleno) return <Navigate to={primeiraRotaDe(modulos)} replace />;
  return <Outlet />;
};

// Rota de cada modulo, para redirecionar quem chegou onde nao alcanca.
const ROTA_DO_MODULO: Record<ModuloAdmin, string> = {
  dashboard: '/admin/dashboard',
  artistas: '/admin/artistas',
  'knowledge-base': '/admin/knowledge-base',
  cupons: '/admin/cupons',
  'pass-access': '/admin/pass-access',
  usuarios: '/admin/usuarios',
  vendas: '/admin/vendas',
  avaliacoes: '/admin/avaliacoes',
  push: '/admin/push',
};

const primeiraRotaDe = (modulos: ModuloAdmin[]): string =>
  modulos.length ? ROTA_DO_MODULO[modulos[0]] : '/artists';

/** Porteiro de um modulo especifico. Conveniencia de navegacao; quem barra e a RLS. */
const RequireModulo: FC<{ modulo: ModuloAdmin }> = ({ modulo }) => {
  const { carregando, podeAcessar, modulos } = useAdminRole();
  if (carregando) return <CarregandoPagina />;
  if (!podeAcessar(modulo)) return <Navigate to={primeiraRotaDe(modulos)} replace />;
  return <Outlet />;
};

// ---- Routes --------------------------------------------------------------------------------

const PublicOnly: FC<{ children: React.ReactNode }> = ({ children }) => {
  const user = useAppSelector((s) => s.auth.user);
  if (user) return <Navigate to='/artists' replace />;
  return <>{children}</>;
};

/**
 * `/artists/:id/negociacoes?n=…` e `/artists/:id/crm?n=…` → `/artists/:id/vendas?n=…`, preservando a
 * consulta. ⚠️ NÃO CORTAR: e-mails, notificações, a Nyta e builds do app já publicados levam os
 * endereços antigos (o Controle de shows virou Vendas e Produção em 27/09/2026).
 */
const ParaOCrm: FC = () => {
  const { id } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/artists/${id}/vendas${search}`} replace />;
};

/** `/artists/:id/modelos` virou as Formações de Vendas › Configurações (03/11/2026); o endereço antigo segue vivo. */
const ParaOsModelos: FC = () => {
  const { id } = useParams();
  return <Navigate to={`/artists/${id}/vendas?aba=configuracoes&parte=formacoes`} replace />;
};

/** `/artists/:id/shows/…` → `/artists/:id/producao/…`, com o resto do caminho e a consulta. */
const ParaAProducao: FC = () => {
  const { id, '*': resto } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/artists/${id}/producao${resto ? `/${resto}` : ''}${search}`} replace />;
};

/**
 * O Diagnóstico REAL, o Planejamento e o Plano de Ação moram no Desenvolvimento
 * (`/artists/:id/desenvolvimento/…`, um menu do SideNav). ⚠️ NÃO CORTAR os endereços antigos
 * (`/diagnostico`, `/action-plan`, `/perfil` e o `?aba=` do Início, que o Início redireciona):
 * notificação gravada, o e-mail semanal, favorito e o app publicado ainda os trazem.
 */
const PAGINA_DA_ABA = { diagnostico: 'diagnostico', planejamento: 'planejamento', execucao: 'plano' } as const;
const ParaAAba: FC<{ aba: keyof typeof PAGINA_DA_ABA }> = ({ aba }) => {
  const { id } = useParams();
  return <Navigate to={`/artists/${id}/desenvolvimento/${PAGINA_DA_ABA[aba]}`} replace />;
};

/**
 * `/artists/:id/marketing` → o Branding. O Marketing virou um grupo do menu em 01/11/2026 (como o
 * Desenvolvimento e o Controle de shows), e a tela de cartões que ele tinha saiu. ⚠️ NÃO CORTAR: o
 * Início do perfil, notificações e o app publicado ainda levam o endereço do módulo.
 */
const ParaOBranding: FC = () => {
  const { id } = useParams();
  return <Navigate to={`/artists/${id}/marketing/branding`} replace />;
};

/**
 * Plano de redes e Gestão das Redes, ocultos desde 01/11/2026, só para o admin da plataforma
 * (Lucas, 02/11/2026). Os outros caem no Branding, como antes. Esconder não protege dado: a RLS segue
 * a mesma para todos.
 */
const SoDoAdmin: FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAdmin = useEhAdmin();
  if (isAdmin === null) return <CarregandoPagina />;
  return isAdmin ? <>{children}</> : <ParaOBranding />;
};

const AppRoutes: FC = () => {
  return (
    <Routes>
      {/* Landing pública (porta de entrada): header/footer próprios, sem AppLayout.
          Renderiza pra todos — o header adapta os CTAs pelo estado de login.

          No app empacotado ela NÃO entra. A landing é peça de marketing: quem baixou o app já
          foi convencido, e uma página de venda dentro do app é exatamente o que a App Review
          chama de "thin wrapper" (4.2). Pior, o "Ver planos" dela leva ao checkout com PIX, e
          caminho de compra fora da loja dentro do app iOS é rejeição por 3.1.1. Dentro do app a
          porta de entrada é o login, e quem já entrou vai direto para os perfis. */}
      <Route path='/' element={<Landing />} />
      {/* O endereço da validação, para quem ficou com ele salvo. */}
      <Route path='/nova-landing' element={<Navigate to='/' replace />} />

      {/* Sobre, Diagnóstico REAL e Music Rio Academy: públicas, na moldura da landing. */}
      <Route path='/sobre' element={<Sobre />} />

      {/* ⚠️ NÃO CORTAR: /diagnostico-real está no rodapé de páginas antigas, no app publicado e em
          links de fora. O Diagnóstico REAL virou uma central como as outras (30/09/2026). */}
      <Route path='/diagnostico-real' element={<Navigate to='/recursos/diagnostico-real' replace />} />

      {/* A central de cada módulo, com um endereço por seção, para a venda mandar a parte que
          responde à pergunta da call (logica/publicas/modulos). */}
      <Route path='/recursos/:modulo' element={<ModuloPublico />} />
      <Route path='/recursos/:modulo/:secao' element={<ModuloPublico />} />

      <Route path='/music-rio-academy' element={<MusicRioAcademy />} />

      {/* A página do workshop de Niterói saiu (27/09/2026, não é mais usada). O endereço estava
          no QR do cartaz impresso: quem ainda o ler cai na página inicial, e não num 404. */}
      <Route path='/niteroi' element={<Navigate to='/' replace />} />

      <Route path='/login' element={<PublicOnly><Login /></PublicOnly>} />
      <Route path='/signup' element={<PublicOnly><Signup /></PublicOnly>} />
      {/* Recuperação de senha (passo 1): tela própria pra digitar o e-mail e disparar o link.
          O link do e-mail cai em /redefinir-senha (passo 2, troca a senha de fato). */}
      <Route path='/esqueci-senha' element={<PublicOnly><ForgotPassword /></PublicOnly>} />

      {/* Redefinir senha: aberta pelo link do e-mail de recuperação (tokens no hash). Pública e
          FORA de PublicOnly de propósito — a própria tela estabelece uma sessão de recovery, e
          PublicOnly redirecionaria pra /artists assim que ela entrasse, sem deixar trocar a senha. */}
      <Route path='/redefinir-senha' element={<ResetPassword />} />

      {/* Retorno do login social (Google). FORA de PublicOnly de propósito: a troca do ?code=
          por sessão precisa rodar antes de qualquer guardião redirecionar. */}
      <Route path='/auth/callback' element={<AuthCallback />} />

      {/* Páginas legais (Termos / Privacidade): públicas e standalone — acessíveis da landing
          por quem ainda não tem conta. Conteúdo único em src/constants/legal.ts. */}
      <Route path='/legal/:slug' element={<Legal />} />

      {Vitrine && <Route path='/__vitrine/:chave?' element={<Vitrine />} />}

      {/* A PROPOSTA QUE O CONTRATANTE ABRE. Pública e sem sessão: quem a lê não tem conta na
          Maestra e não vai criar uma para ler um documento que é dela. Fica FORA do
          `PublicOnly` de propósito — uma artista logada precisa poder conferir o próprio link
          sem ser mandada de volta para /artists. */}
      <Route path='/proposta/:token' element={<PropostaPublica />} />
      {/* A ficha de contrato da negociação (Vendas v1.2 §3): o link do botão "Enviar ficha de
          contrato" e o comprovante do aceite. A mesma página da proposta, que lê o tipo do link. */}
      <Route path='/aceite/:token' element={<PropostaPublica />} />
      <Route path='/ficha/:token' element={<FichaPublica />} />
      <Route path='/equipe/:token' element={<CronogramaPublico />} />

      {/* A PÁGINA PÚBLICA DO ARTISTA (o "link na bio") e o "Contrate meu show", que cai no funil de
          Vendas. Sem sessão, e fora do `PublicOnly` pelo mesmo motivo da proposta: o artista logado
          confere a própria página. */}
      <Route path='/a/:slug' element={<PerfilDoArtista />} />
      <Route path='/a/:slug/contratar' element={<PerfilDoArtista />} />
      <Route path='/a/:slug/shows' element={<PerfilDoArtista />} />
      <Route path='/a/:slug/presskit' element={<PressKitPublico />} />
      <Route path='/a/:slug/presskit/fotos' element={<FotosDoPressKit />} />
      <Route path='/a/:slug/show/:produto' element={<MaterialDeVendaPublico />} />
      {/* O plano de redes que o artista manda pra equipe (Gestão das Redes v1): só com o token. */}
      <Route path='/a/:slug/redes/:token' element={<RedesPublico />} />
      {/* A playlist compartilhada: pública com link, ou só a equipe (o edge confere a sessão). */}
      <Route path='/ouvir/:token' element={<PlaylistPublica />} />
      {/* O documento de branding compartilhado (o fotógrafo, o designer e a equipe). */}
      <Route path='/branding/:token' element={<BrandingPublico />} />

      <Route element={<RequireAuth />}>
        {/* Coleta de maioridade e aceite: autenticadas, mas FORA do RequireConsent — dentro dele
            o gate mandaria a pessoa para a tela de consentimento a partir da própria tela de
            consentimento, em laço. */}
        <Route path='/consentimento' element={<Consent />} />
        <Route path='/conta-bloqueada' element={<ContaBloqueada />} />

        <Route element={<RequireConsent />}>
        {/* Boas-vindas pós-cadastro: autenticada, mas em tela cheia (sem o layout do app). */}
        <Route path='/welcome' element={<Welcome />} />
        {/* Criação de artista: chat full-screen (sem o layout do app). O perfil nasce
            no diagnóstico (não-pago); o pagamento acontece na tela de desbloqueio. */}
        <Route path='/criar-artista' element={<ArtistCreate />} />
        {/* Desbloqueio: diagnóstico salvo + pagamento (tela cheia, perfil ainda não-pago). */}
        <Route path='/artists/:id/desbloquear' element={<ProfileUnlock />} />
        {/* Sucesso da assinatura: tela cheia, centralizada (sem sidebar nem banner) — todo o
            destaque pra confirmação do pagamento. */}
        <Route path='/planos/sucesso' element={<SubscriptionSuccessPage />} />
        {/* ⚠️ O ENDEREÇO ANTIGO NÃO PODE MORRER. Ver o par dele, mais abaixo. */}
        <Route path='/assinatura/sucesso' element={<Navigate to='/planos/sucesso' replace />} />

        {/* Refazer diagnóstico (PRO): é o MESMO quiz de /criar-artista em modo "redo", e
            portanto tela cheia. Estava dentro do AppLayout e aparecia espremido no container
            da página, com a barra de busca e o rail em volta de um fluxo que pede a tela
            inteira. O gate continua: RequireArtistActive lê o :id da URL e funciona fora do
            layout. */}
        {/* Planejamento Estratégico (Wizard): conversa que pede a tela inteira, com coluna de
            contexto própria (vídeo da etapa + plano acumulado). Dentro do AppLayout ela dividia
            espaço com o rail, o topbar e o painel de perfil, e a coluna de resultados precisava
            ser renderizada LÁ, por um store global, só para virar uma 3ª coluna do grid.
            Fora do layout o Wizard desenha o próprio shell e é dono do painel — mesmo caminho do
            "refazer diagnóstico" abaixo. O gate continua: RequireArtistActive lê o :id da URL. */}
        <Route element={<RequireArtistActive feature='planning' espera='app' />}>
          <Route path='/artists/:id/wizard/*' element={<Wizard />} />
        </Route>
        <Route element={<RequireArtistActive feature='refazer' espera='app' />}>
          <Route path='/artists/:id/diagnostico/refazer' element={<ArtistCreate />} />
        </Route>
        {/* Montar o branding: a conversa com a Nyta em tela cheia, como o wizard. */}
        <Route element={<RequireArtistActive feature='marketing' espera='app' />}>
          {/* Cada página do Marketing pede a sua no acesso do convidado (03/11/2026). */}
          <Route element={<RequireArtistActive feature='marketing' pagina='branding' espera='app' />}>
            <Route path='/artists/:id/marketing/branding/criar' element={<CriarBranding />} />
          </Route>
          <Route element={<RequireArtistActive feature='marketing' pagina='material' espera='app' />}>
            <Route path='/artists/:id/marketing/material/criar' element={<CriarMaterial />} />
          </Route>
          {/* ⚠️ NÃO CORTAR: Plano de redes e Gestão das Redes estão ocultos desde 01/11/2026 (reunião
              com a Anita: voltam depois de refeitos), e abertos só para o admin desde 02/11. Os
              endereços seguem vivos em lembretes, e-mails e eventos da Agenda: levam ao Branding. */}
          <Route path='/artists/:id/marketing/redes/configurar' element={<SoDoAdmin><CriarRedes /></SoDoAdmin>} />
          <Route path='/artists/:id/marketing/redes/*' element={<ParaOBranding />} />
        </Route>

        {/* O editor da gravação é uma tela cheia e escura, fora da casca, como o Wizard. */}
        <Route element={<RequireArtistActive feature='catalog' espera='app' />}>
          <Route path='/artists/:id/catalog/projects/:projectId' element={<EditorDaMusica />} />
        </Route>
        <Route element={<Casca />}>
          {/* Já redesenhadas (src/ui-carbon). */}
          <Route path='/notifications' element={<Notifications />} />
          <Route path='/settings' element={<Settings />} />
          <Route path='/suporte' element={<Support />} />
          <Route path='/artists' element={<Artists />} />
          {/* O Financeiro ainda não existe: a página diz o que vem, sem trava nem preço. */}
          <Route path='/artists/:id/financeiro' element={<EmBreve area='financeiro' />} />
          {/* O Marketing (parte 1: branding), módulo PRO com acesso por módulo do membro. */}
          <Route element={<RequireArtistActive feature='marketing' />}>
            <Route path='/artists/:id/marketing' element={<ParaOBranding />} />
            <Route element={<RequireArtistActive feature='marketing' pagina='branding' />}>
              <Route path='/artists/:id/marketing/branding' element={<BrandingDoMarketing />} />
            </Route>
            <Route element={<RequireArtistActive feature='marketing' pagina='material' />}>
              <Route path='/artists/:id/marketing/material' element={<MaterialDeApresentacao />} />
              <Route path='/artists/:id/marketing/material/venda' element={<MaterialDeVenda />} />
            </Route>
            <Route path='/artists/:id/marketing/redes/plano' element={<SoDoAdmin><PlanoDeRedes /></SoDoAdmin>} />
            <Route path='/artists/:id/marketing/redes' element={<SoDoAdmin><GestaoDasRedes /></SoDoAdmin>} />
          </Route>
          {/* ⚠️ NÃO CORTAR: o dossiê (/perfil) repetia a aba Planejamento do Início e virou ela. */}
          <Route path='/artists/:id/perfil' element={<ParaAAba aba='planejamento' />} />
          {/* O Início: o resumo do perfil (o método e um número de cada módulo). Livre, e de
              propósito: quem acabou de se cadastrar alcança a home e o diagnóstico sem pagar. */}
          <Route path='/artists/:id' element={<Inicio />} />
          {/* Livres para o dono; o convidado entra se o titular liberou o Desenvolvimento. */}
          <Route element={<RequireModuloLiberado feature='plano' />}>
            <Route path='/artists/:id/desenvolvimento/diagnostico' element={<Diagnostico />} />
            <Route path='/artists/:id/desenvolvimento/planejamento' element={<Planejamento />} />
            {/* Livre de propósito, como os outros dois: o plano diz sozinho o que é do PRO (a oferta
                no lugar da ação), e acompanhar vale para quem tem o nível de plano. */}
            <Route path='/artists/:id/desenvolvimento/plano' element={<PlanoDeAcao />} />
          </Route>
          <Route path='/artists/:id/desenvolvimento' element={<ParaAAba aba='diagnostico' />} />
          <Route path='/artists/:id/diagnostico' element={<ParaAAba aba='diagnostico' />} />
          <Route path='/artists/:id/action-plan' element={<ParaAAba aba='execucao' />} />
          <Route element={<RequireArtistActive feature='team' />}>
            <Route path='/artists/:id/team' element={<Team />} />
          </Route>
          <Route element={<RequireArtistActive feature='arquivos' />}>
            <Route path='/artists/:id/arquivos' element={<Arquivos />} />
          </Route>
          <Route element={<RequireArtistActive feature='agenda' />}>
            <Route path='/artists/:id/agenda' element={<Agenda />} />
          </Route>
          <Route element={<RequireArtistActive feature='negociacoes' />}>
            <Route path='/artists/:id/vendas' element={<Negociacoes />} />
          </Route>
          {/* O Perfil (a página pública do artista) é do plano grátis (decisão do Lucas, 04/10/2026):
              fora do bloqueio de Vendas, com o portão só do acesso por módulo. O que é do PRO nele, o
              "Contrate meu show", a própria tela trava (e a edge `perfil-publico` também). */}
          <Route element={<RequireModuloLiberado feature='perfil' />}>
            <Route path='/artists/:id/perfil-publico' element={<PerfilPublico />} />
          </Route>
          {/* Os Lançamentos (03/11/2026): página própria no acesso da Equipe. */}
          <Route element={<RequireArtistActive feature='catalog' pagina='lancamentos' />}>
            <Route path='/artists/:id/lancamentos' element={<Lancamentos />} />
            <Route path='/artists/:id/lancamentos/:lancamentoId' element={<LancamentoLigado />} />
          </Route>
          <Route element={<RequireArtistActive feature='catalog' />}>
            <Route path='/artists/:id/catalog' element={<Catalog />} />
          </Route>
          <Route element={<RequireArtistActive feature='shows' />}>
            <Route path='/artists/:id/producao' element={<Shows />} />
            <Route path='/artists/:id/producao/:showId' element={<PaginaDoShow />} />
            <Route path='/artists/:id/producao/:showId/cronograma' element={<CronogramaDoShow />} />
            <Route path='/artists/:id/modelos' element={<ParaOsModelos />} />
          </Route>
          {/* O cronograma do show para a equipe, só para ler (03/11/2026): o músico escalado vê sem
              acesso à Produção. Sem módulo no convite: a função do banco escolhe os shows. */}
          <Route element={<RequireArtistActive feature='cronograma' />}>
            <Route path='/artists/:id/cronograma' element={<CronogramaDaEquipe />} />
          </Route>

          {/* A Nyta (Carbon for AI): a conversa inteira, com o rótulo de IA. */}
          <Route element={<RequireArtistActive feature='nyta' />}>
            <Route path='/artists/:id/nyta' element={<Nyta />} />
          </Route>

          {/* O dinheiro (onda 6): planos, ajustar o plano, o PIX e o histórico, no Carbon. */}
          {/* ⚠️ ERA `/assinatura`, E O NOME MUDOU PARA SER DIGITÁVEL.
              O app nativo deixou de vender: ele mostra os planos e diz, em texto, onde se
              assina — e quem lê tem de conseguir escrever o endereço na barra do navegador.
              "maestramanager.com/planos" cabe numa frase; "/assinatura" era mais uma palavra
              para soletrar ao telefone. */}
          <Route path='/planos' element={<SubscriptionPage />} />
          {/* Ajustar as quantidades de add-on de quem JÁ assina. Fica fora da /planos porque a
              /planos é vitrine e tem gate que devolve assinante ativo para as Configurações. */}
          <Route path='/planos/ajustar' element={<AjustarPlanoPage />} />
          {/* ⚠️ E O ENDEREÇO ANTIGO FICA, PARA SEMPRE — isto não é zelo.
              Dois emissores continuam a apontar para ele depois desta linha: a edge function
              `checkout-handoff`, que é deployada à parte deste bundle e passa uma janela
              dessincronizada a cada deploy; e os builds do app JÁ PUBLICADOS, que têm
              `${SITE}/assinatura` compilado lá dentro e vão abri-lo enquanto existirem
              telemóveis com eles instalados. Cortar isto transforma os dois num 404. */}
          <Route path='/assinatura' element={<Navigate to='/planos' replace />} />
          <Route path='/pagamento' element={<PaymentPage />} />
          <Route path='/pagamentos' element={<Payments />} />
          {/* ⚠️ TELAS AINDA NO DESENHO ANTIGO. Cada rota dentro de <Legado> espera o redesenho no
              Carbon (docs/redesenho/telas.md); migrar uma tela é tirar a rota daqui e apontá-la
              para src/ui-carbon. Quando este bloco ficar vazio, <Legado> é apagado. */}
          <Route element={<Legado />}>


          {/* Os módulos do PRO. Cada gate leva a CHAVE do seu bloqueio: quem não assina entra,
              vê o app em volta e encontra a tela do módulo dizendo o que ele faz — em vez de ser
              jogado para fora sem nunca ver o produto. */}
          {/* ⚠️ NÃO CORTAR. As notificações do CRM (banco e `negociacoes-lembretes`) nasceram com
              `/negociacoes?n=`, rota que nunca existiu: o CRM é `/crm`. As fontes foram corrigidas
              em 26/09/2026, mas um aviso já gravado (e o app publicado) ainda pode trazer o endereço
              antigo, e ele tem de cair na ficha certa. */}
          <Route path='/artists/:id/negociacoes' element={<ParaOCrm />} />
          {/* ⚠️ NÃO CORTAR. O CRM era `/crm` e os Shows `/shows` até 27/09/2026 (viraram Vendas e
              Produção): avisos gravados, a Nyta, os e-mails e o app publicado ainda levam esses. */}
          <Route path='/artists/:id/crm' element={<ParaOCrm />} />
          <Route path='/artists/:id/shows/*' element={<ParaAProducao />} />
          {/* /profile foi fundido na home do artista (Dashboard) */}
          <Route path='/artists/:id/profile' element={<Navigate to='..' relative='path' replace />} />

          <Route element={<RequireAdmin />}>
            {/* O CRM de vendas fica FORA do RequireFullAdmin: e a unica tela do admin que o time
                de vendas alcanca. */}
            <Route element={<RequireModulo modulo='vendas' />}>
              <Route path='/admin/vendas' element={<AdminSales />} />
            </Route>
            {/* O painel de ativacao virou a aba Inbound de /admin/vendas. A rota antiga
                redireciona para nao quebrar link salvo por quem ja usava. */}
            <Route path='/admin/crm' element={<Navigate to='/admin/vendas' replace />} />

            <Route element={<RequireModulo modulo='dashboard' />}>
              <Route path='/admin/dashboard' element={<AdminDashboard />} />
            </Route>
            <Route element={<RequireModulo modulo='knowledge-base' />}>
              <Route path='/admin/knowledge-base' element={<AdminKnowledgeBase />} />
            </Route>
            <Route element={<RequireModulo modulo='cupons' />}>
              <Route path='/admin/cupons' element={<AdminCoupons />} />
            </Route>
            <Route element={<RequireModulo modulo='pass-access' />}>
              <Route path='/admin/pass-access' element={<AdminAccessPasses />} />
            </Route>
            <Route element={<RequireModulo modulo='usuarios' />}>
              <Route path='/admin/usuarios' element={<AdminUsers />} />
            </Route>
            <Route element={<RequireModulo modulo='artistas' />}>
              <Route path='/admin/artistas' element={<AdminArtists />} />
              <Route path='/admin/artistas/:artistId' element={<AdminArtists />} />
            </Route>
            <Route element={<RequireModulo modulo='avaliacoes' />}>
              <Route path='/admin/avaliacoes' element={<AdminReviews />} />
            </Route>
            <Route element={<RequireModulo modulo='push' />}>
              <Route path='/admin/push' element={<AdminPush />} />
            </Route>

            {/* Acessos fica fora do esquema de modulos DE PROPOSITO: se ela fosse concedivel,
                quem a recebesse poderia conceder tudo a si mesmo. So admin pleno. */}
            <Route element={<RequireFullAdmin />}>
              <Route path='/admin/acessos' element={<AdminAcessos />} />
              {/* Trocar o modelo da IA muda a Nyta de todo mundo: também só admin pleno. */}
              <Route path='/admin/modelos' element={<AdminModelosDaIa />} />
            </Route>
          </Route>
          </Route>
          <Route path='*' element={<Page404 />} />
        </Route>
        </Route>
      </Route>
    </Routes>
  );
};

const RootComponent: FC = () => {
  const language = useAppSelector((state) => state.language.language);

  useEffect(() => {
    document.documentElement.setAttribute('lang', language);
    i18next.changeLanguage(language);
  }, [language]);

  return (
    <Router>
      <AuthListener />
      <RecoveryHashGuard />
      {/* A abertura (e as telas cheias fora da casca) espera com a marca; dentro da casca, a casca
          fica e a espera é o esqueleto da página (o Suspense de lá). */}
      <Suspense fallback={<CarregandoApp />}>
        <AppRoutes />
      </Suspense>
      {/* O modal do código de acesso: um só, aberto de qualquer tela (`abrirCodigoDeAcesso`). */}
      <ModalDoCodigoLigado />
    </Router>
  );
};

function App() {
  return (
    <TemaProvider>
      {/* A abertura toca até o nome "Maestra", por cima de tudo, mesmo que o app fique pronto antes. */}
      <AberturaDaMarca />
      {/* Os avisos soltos (`message` de ui-carbon/padroes/avisos), no ToastNotification do Carbon. */}
      <AvisosDoCarbon />
      <Provider store={store}>
        {/* A reidratação da loja também é abertura: a marca, e não um quadro vazio. */}
        <PersistGate loading={<CarregandoApp />} persistor={persistor}>
          <RootComponent />
        </PersistGate>
      </Provider>
    </TemaProvider>
  );
}

export default App;
